import { Globe } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SUPPORTED_LOCALES } from "@/i18n";
import { localizePath, splitLocale } from "@/lib/locale";

/* Nivel 1: al cambiar de idioma navegamos a la URL equivalente (/es/pricing ↔
   /de/pricing). Nivel 2 (webs de organizador, subastas): la URL canónica no
   cambia, solo el idioma de la UI. */
const LEVEL1_RE = /^\/(how-it-works|auctions|pricing|about|contact|resources|legal)(\/|$)|^\/$/;

export const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const current = i18n.resolvedLanguage || "en";

  const selectLocale = (code) => {
    i18n.changeLanguage(code);
    const { path } = splitLocale(location.pathname);
    if (LEVEL1_RE.test(path)) {
      const target = localizePath(path, code) + location.search + location.hash;
      if (target !== location.pathname + location.search + location.hash) {
        navigate(target);
      }
    }
  };

  return (
    /* Controlled open + click toggle: Radix's pointerdown toggling is unreliable
       on iOS Safari (radix-ui/primitives#2580) — this keeps the selector tappable
       on every device, including published microsites viewed on phones. */
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        data-testid="language-switcher-trigger"
        aria-label={t("common.language")}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 h-8 text-xs font-normal text-black/70 hover:text-black hover:bg-black/5 transition-colors duration-200"
      >
        <Globe className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="uppercase tracking-wide">{current}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl border-black/5 shadow-[0_16px_40px_rgb(0,0,0,0.08)] min-w-[10rem]">
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale.code}
            data-testid={`language-option-${locale.code}`}
            onClick={() => selectLocale(locale.code)}
            className={`rounded-lg cursor-pointer ${
              current === locale.code ? "font-semibold text-[#1D4D3B]" : ""
            }`}
          >
            {locale.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
